import {
  FiTrash,
  FiTag,
  FiGlobe,
  FiLock,
  FiTarget,
  FiEdit3,
} from "react-icons/fi";
import { LuBrain, LuSparkles } from "react-icons/lu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/shadcn/tooltip";
import Button from "@/components/ui/Button";
import { GENERATION_COUNT_OPTIONS } from "@kagami/shared/generation";
import {
  ArtifactVisibility,
  ARTIFACT_VISIBILITIES,
} from "@kagami/shared/artifactVisibility";
import {
  ARTIFACT_SCOPES,
  ARTIFACT_SCOPE_LABELS,
} from "@kagami/shared/artifactScope";
import { UserOwns } from "@/contexts/UserContext";
import { HiMiniRectangleGroup } from "react-icons/hi2";

/**
 * Bottom row: the model's setting inputs, the Smart toggle and the Generate
 * group. Deliberately never wraps. The model picker itself lives in
 * AiModelInput's own header now, not here.
 *
 * `settings` is a rendered node (the container maps `SettingInput`). Smart and
 * default mode each get their own Generate button so the two workflows stay apart:
 * smart generates in one shot, and its Preview hands the expanded prompt to the
 * default view instead. `previewing` is tracked apart from `submitting` so an
 * in-flight preview leaves Generate usable.
 */
export default function ActionBar({
  isEditingArtifact,
  loadedArtifact,
  hasLoadedArtifact,
  artifactHasChanges,
  canSaveArtifact,
  artifactVisibility,
  onArtifactVisibilityChange,
  artifactScope,
  onArtifactScopeChange,
  onSaveArtifact,
  onSaveArtifactAs,
  onCancelArtifact,
  onDeleteArtifact,
  model,
  settings,
  smartMode,
  onSmartModeChange,
  hasStructuringReasoning,
  onShowStructuringReasoning,
  submitting,
  previewing,
  canGenerate,
  onGenerate,
  onSmartGenerate,
  onSmartPreview,
  totalGenerations,
  onTotalGenerationsChange,
}) {
  // Capped a second time against the model's own max_generations — the shared
  // constant only bounds the global request limit, not what this model allows.
  const generationCountOptions = GENERATION_COUNT_OPTIONS.filter(
    (count) => count <= (model?.max_generations ?? 1),
  );

  // Editing an artifact swaps the whole row for a dedicated actions block —
  // the model picker, its settings and Generate don't apply to editing a
  // saved artifact. A brand-new artifact only gets Save (which opens the
  // save dialog); an already-loaded one also gets Save As (dialog, saves a
  // copy) since its own Save just overwrites it in place with no dialog.
  if (isEditingArtifact) {
    // Styled to match SettingInput's "options" dropdowns (icon + no-chevron
    // trigger) so artifact scope/visibility read as settings, not toggles.
    // Shared by both a brand-new artifact (no owner yet — always editable)
    // and an already-loaded one (gated below by UserOwns).
    const scopeSelect = (
      <Select value={artifactScope} onValueChange={onArtifactScopeChange}>
        <SelectTrigger
          id="artifact-scope"
          title="Artifact scope"
          className="text-xs rounded-sm border-0 !h-6 px-2 select-none gap-1.5 min-w-24 capitalize justify-start"
          showChevron={false}>
          <span className="text-secondary-foreground/70 shrink-0">
            <HiMiniRectangleGroup className="w-3 h-3" />
          </span>
          <SelectValue placeholder="Scope" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {ARTIFACT_SCOPES.map((option) => (
              <SelectItem
                id={`ActionBar__artifactScopeOption-${option}`}
                key={option}
                value={option}
                className="text-xs">
                {ARTIFACT_SCOPE_LABELS[option]}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    );

    const visibilitySelect = (
      <Select
        value={artifactVisibility}
        onValueChange={onArtifactVisibilityChange}>
        <SelectTrigger
          id="artifact-visibility"
          title="Artifact visibility"
          className="text-xs rounded-sm border-0 !h-6 px-2 select-none gap-1.5 min-w-20 capitalize justify-start"
          showChevron={false}>
          <span className="text-secondary-foreground/70 shrink-0">
            {artifactVisibility === ArtifactVisibility.Public ? (
              <FiGlobe className="w-3 h-3" />
            ) : (
              <FiLock className="w-3 h-3" />
            )}
          </span>
          <SelectValue placeholder="Visibility" />
        </SelectTrigger>
        <SelectContent side="top">
          <SelectGroup>
            {ARTIFACT_VISIBILITIES.map((option) => (
              <SelectItem
                id={`ActionBar__artifactVisibilityOption-${option}`}
                key={option}
                value={option}
                className="text-xs capitalize">
                {option}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    );

    return (
      <div id="ActionBar" className="p-3">
        <div
          id="ActionBar__artifactSettings"
          className="flex gap-2 overflow-x-auto w-full pb-4">
          {hasLoadedArtifact ? (
            <UserOwns
              id={loadedArtifact?.user?.id}
              fallback={
                <div className="text-xs rounded-sm !h-6 px-2 select-none gap-1.5 min-w-20 capitalize justify-start text-secondary-foreground/70 flex items-center gap-1.5 border">
                  <HiMiniRectangleGroup className="w-3 h-3" />
                  <span className="capitalize">
                    {ARTIFACT_SCOPE_LABELS[loadedArtifact.scope]}
                  </span>
                </div>
              }>
              {scopeSelect}
            </UserOwns>
          ) : (
            scopeSelect
          )}

          {hasLoadedArtifact ? (
            <UserOwns id={loadedArtifact?.user?.id}>
              {visibilitySelect}
            </UserOwns>
          ) : (
            visibilitySelect
          )}
        </div>
        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex gap-2">
            {hasLoadedArtifact && (
              <UserOwns
                id={loadedArtifact?.user?.id}
                fallback={
                  <span
                    className="text-[10px] text-secondary-foreground/70 h-6 flex items-center px-2 select-none"
                    title="Only the owner can delete this artifact">
                    {loadedArtifact?.user?.email}
                  </span>
                }>
                <Button
                  id="ActionBar__deleteArtifact"
                  type="danger-ghost"
                  className="h-6 rounded-sm"
                  onClick={onDeleteArtifact}
                  title="Delete this artifact">
                  Delete
                </Button>
              </UserOwns>
            )}
          </div>
          <div className="flex gap-2">
            {hasLoadedArtifact && (
              <Button
                id="ActionBar__saveArtifactAs"
                type="secondary"
                className="h-6 rounded-sm"
                onClick={onSaveArtifactAs}
                disabled={!canSaveArtifact}>
                Save As
              </Button>
            )}
            {(() => {
              const saveButton = (
                <Button
                  id="ActionBar__saveArtifact"
                  type="primary"
                  className="h-6 rounded-sm"
                  onClick={onSaveArtifact}
                  disabled={!canSaveArtifact}>
                  Save
                </Button>
              );
              // A brand-new artifact has no owner to gate against yet.
              return hasLoadedArtifact ? (
                <UserOwns id={loadedArtifact?.user?.id}>{saveButton}</UserOwns>
              ) : (
                saveButton
              );
            })()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ActionBar" className="flex flex-col px-3 pb-3">
      <div
        id="ActionBar__settings"
        className="flex gap-2 overflow-x-auto w-full pb-4">
        {settings}
      </div>
      {/* Takes the row's slack so the settings sit against the left rather
                than being centred between two growing siblings. */}
      {/* <div id="ActionBar__spacer" className="flex-1 min-w-0" /> */}
      <div
        className="flex justify-between gap-2 border-t pt-4"
        id="ActionBar__generateActions">
        <div className="flex gap-2">
          <Select
            value={smartMode ? "artifacts" : "manual"}
            onValueChange={(v) => onSmartModeChange(v === "artifacts")}>
            <SelectTrigger
              id="smart-mode"
              title={
                smartMode
                  ? "Smart mode — mentions, saved prompts and prompt preview"
                  : "Default mode — plain prompt and asset pickers"
              }
              className="text-xs rounded-sm border-0 !h-6 px-2 select-none gap-1.5 min-w-24 justify-start"
              showChevron={false}>
              <span className="text-secondary-foreground/70 shrink-0">
                <LuSparkles className="w-3 h-3" />
              </span>
              <SelectValue placeholder="Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem
                  id="ActionBar__modeOption-manual"
                  value="manual"
                  className="text-xs">
                  Manual
                </SelectItem>
                <SelectItem
                  id="ActionBar__modeOption-artifacts"
                  value="artifacts"
                  className="text-xs">
                  Artifacts
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          {smartMode && (
            <Button
              id="ActionBar__preview"
              type="secondary"
              className="h-6 rounded-sm shrink-0"
              onClick={onSmartPreview}
              disabled={!canGenerate || previewing}
              title="Expand the prompt into the default view">
              Preview
            </Button>
          )}
        </div>

        <div
          id="ActionBar__generate"
          className={`flex ${canGenerate ? "" : "opacity-60 pointer-events-none"}`}>
          {smartMode ? (
            <Button
              type="primary"
              className="rounded-r-none border-0 h-6 rounded-sm"
              onClick={onSmartGenerate}
              title="Generate straight from this prompt">
              {submitting ? "Submitting..." : "Generate"}
            </Button>
          ) : (
            <Button
              type="primary"
              className="rounded-r-none border-0 h-6 rounded-sm"
              onClick={onGenerate}>
              {submitting ? "Submitting..." : "Generate"}
            </Button>
          )}
          {model?.max_generations > 1 && (
            <Select
              value={String(totalGenerations)}
              onValueChange={(v) => onTotalGenerationsChange(parseInt(v, 10))}>
              <SelectTrigger
                id="ActionBar__generations"
                className="hover:bg-primary/90 cursor-pointer !border-l border-background/10 rounded-l-none rounded-r-sm border-0 !h-6 text-xs select-none bg-primary text-primary-foreground"
                showChevron={false}>
                <SelectValue placeholder="1" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {generationCountOptions.map((i) => (
                    <SelectItem
                      id={`ActionBar__generationsOption--${i}`}
                      key={i}
                      value={String(i)}
                      className="text-xs">
                      {i}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  );
}
